import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { HStack, VStack, Text, Input, NumberInput, NumberInputField, Box, Checkbox } from '@chakra-ui/react';
import { useAppStore } from '../../state/store';
import { AppDialog, DialogButton } from '../common/AppDialog';
import {
    useAnnotationScaleStore,
    annoScalesOf,
    applyObjectScale,
    isAnnotativeType,
} from '../../state/annotationScaleStore';

/**
 * Object Scale dialog (Faz 8.4 — OBJECTSCALE parity): annotative membership
 * of the selected entities against the scale list. Checkbox = the entity
 * belongs to that scale; toggling applies add/remove as ONE undo step.
 * Reset (ANNORESET) collapses memberships back to the native scale.
 */
export function ObjectScaleDialog() {
    const { t } = useTranslation();
    const isOpen = useAppStore((state) => state.manageDialog === 'objectScale');
    const onClose = useAppStore((state) => state.closeManageDialog);
    const selectedIds = useAppStore((state) => state.selectedIds);
    const entities = useAppStore((s) => s.entities);
    const scaleList = useAnnotationScaleStore((s) => s.scaleList);
    const currentScale = useAnnotationScaleStore((s) => s.currentScale);
    const setCurrentScale = useAnnotationScaleStore((s) => s.setCurrentScale);

    const selected = selectedIds.map((id) => entities[id]).filter(Boolean);
    const eligible = selected.filter((e) => isAnnotativeType(e.type));
    const membershipOf = (scaleName: string): boolean | 'mixed' => {
        if (eligible.length === 0) return false;
        const states = eligible.map((e) => scaleName in (annoScalesOf(e.data) ?? {}));
        return states.every(Boolean) ? true : states.some(Boolean) ? 'mixed' : false;
    };

    const toggle = (scaleName: string, on: boolean) => {
        applyObjectScale(eligible.map((e) => e.id), on ? 'add' : 'remove', scaleName);
    };
    const reset = () => applyObjectScale(eligible.map((e) => e.id), 'reset');

    return (
        <AppDialog
            isOpen={isOpen}
            onClose={onClose}
            title={t('ui.cad.objectScale.title')}
            size="md"
            footer={<DialogButton variant="ghost" onClick={onClose}>{t('common.cancel')}</DialogButton>}
        >
            <VStack spacing={3} align="stretch">
                <Text fontSize="xs" color="var(--color-text-secondary)">
                    {eligible.length > 0
                        ? t('ui.cad.objectScale.selectedCount', { count: eligible.length })
                        : t('ui.cad.objectScale.noSelection')}
                </Text>
                <Box borderWidth="1px" borderColor="var(--color-border)" borderRadius="var(--cad-radius-sm)" maxH="220px" overflowY="auto" p={1}>
                    {scaleList.map((s) => {
                        const state = membershipOf(s.name);
                        return (
                            <HStack key={s.name} px={2} py={1} justify="space-between">
                                <Checkbox
                                    size="sm"
                                    isChecked={state === true}
                                    isIndeterminate={state === 'mixed'}
                                    isDisabled={eligible.length === 0}
                                    onChange={(e) => toggle(s.name, e.target.checked)}
                                >
                                    <Text fontSize="sm">{s.name}</Text>
                                </Checkbox>
                                <HStack spacing={1}>
                                    <Text fontSize="xs" color="var(--color-text-secondary)">
                                        {t('ui.cad.objectScale.factor', { factor: s.factor })}
                                    </Text>
                                    <DialogButton
                                        variant={s.name === currentScale ? 'primary' : 'secondary'}
                                        onClick={() => setCurrentScale(s.name)}
                                    >
                                        {s.name === currentScale ? t('ui.cad.objectScale.current') : t('ui.cad.objectScale.makeCurrent')}
                                    </DialogButton>
                                </HStack>
                            </HStack>
                        );
                    })}
                </Box>
                <HStack justify="space-between">
                    <DialogButton onClick={reset} isDisabled={eligible.length === 0}>
                        {t('ui.cad.objectScale.reset')}
                    </DialogButton>
                    <Text fontSize="xs" color="var(--color-text-secondary)">
                        {t('ui.cad.objectScale.currentLabel', { scale: currentScale })}
                    </Text>
                </HStack>
            </VStack>
        </AppDialog>
    );
}

/**
 * Scale List Editor (Faz 8.4 — SCALELISTEDIT parity): add/rename/delete
 * scales and set the current annotation scale.
 */
export function ScaleListEditDialog() {
    const { t } = useTranslation();
    const isOpen = useAppStore((state) => state.manageDialog === 'scaleListEdit');
    const onClose = useAppStore((state) => state.closeManageDialog);
    const scaleList = useAnnotationScaleStore((s) => s.scaleList);
    const currentScale = useAnnotationScaleStore((s) => s.currentScale);
    const setCurrentScale = useAnnotationScaleStore((s) => s.setCurrentScale);
    const addScale = useAnnotationScaleStore((s) => s.addScale);
    const removeScale = useAnnotationScaleStore((s) => s.removeScale);
    const renameScale = useAnnotationScaleStore((s) => s.renameScale);
    const [newName, setNewName] = useState('1:250');
    const [newFactor, setNewFactor] = useState(250);
    const [editing, setEditing] = useState<string | null>(null);
    const [editName, setEditName] = useState('');

    return (
        <AppDialog
            isOpen={isOpen}
            onClose={onClose}
            title={t('ui.cad.scaleList.title')}
            size="md"
            footer={<DialogButton variant="ghost" onClick={onClose}>{t('common.cancel')}</DialogButton>}
        >
            <VStack spacing={3} align="stretch">
                <Box borderWidth="1px" borderColor="var(--color-border)" borderRadius="var(--cad-radius-sm)" maxH="260px" overflowY="auto" p={1}>
                    {scaleList.map((s) => (
                        <HStack key={s.name} px={2} py={1} justify="space-between">
                            {editing === s.name ? (
                                <Input
                                    size="xs"
                                    value={editName}
                                    autoFocus
                                    onChange={(e) => setEditName(e.target.value)}
                                    onBlur={() => { renameScale(s.name, editName); setEditing(null); }}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') { renameScale(s.name, editName); setEditing(null); }
                                    }}
                                    w="140px"
                                />
                            ) : (
                                <HStack spacing={2}>
                                    <Text fontSize="sm" fontWeight={s.name === currentScale ? 'bold' : 'normal'}>
                                        {s.name}
                                    </Text>
                                    <Text fontSize="xs" color="var(--color-text-secondary)">
                                        {t('ui.cad.objectScale.factor', { factor: s.factor })}
                                    </Text>
                                </HStack>
                            )}
                            <HStack spacing={1}>
                                <DialogButton variant="secondary" onClick={() => { setEditing(s.name); setEditName(s.name); }}>
                                    {t('ui.cad.scaleList.rename')}
                                </DialogButton>
                                <DialogButton variant="secondary" onClick={() => removeScale(s.name)}>
                                    {t('ui.cad.scaleList.delete')}
                                </DialogButton>
                                <DialogButton
                                    variant={s.name === currentScale ? 'primary' : 'secondary'}
                                    onClick={() => setCurrentScale(s.name)}
                                >
                                    {t('ui.cad.objectScale.makeCurrent')}
                                </DialogButton>
                            </HStack>
                        </HStack>
                    ))}
                </Box>
                <HStack justify="space-between">
                    <Input
                        size="sm"
                        value={newName}
                        w="140px"
                        bg="var(--color-bg-primary)"
                        borderColor="var(--color-border)"
                        onChange={(e) => setNewName(e.target.value)}
                        placeholder="1:250"
                    />
                    <NumberInput size="sm" w="110px" value={newFactor} min={0.01} step={10}
                        onChange={(_, v) => setNewFactor(Number.isFinite(v) ? v : newFactor)}>
                        <NumberInputField bg="var(--color-bg-primary)" borderColor="var(--color-border)" />
                    </NumberInput>
                    <DialogButton variant="primary" onClick={() => { addScale(newName, newFactor); }}>
                        {t('ui.cad.scaleList.add')}
                    </DialogButton>
                </HStack>
            </VStack>
        </AppDialog>
    );
}
